import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { MitelIntegrations, ThirdPartyIntegrations, GeneralPrivileges, Integration, Privilege, AccountBasedIntegrations, IntegrationAsyncOperationInf } from '../interfaces/integrations';
import { TranslateService } from '@ngx-translate/core';
import { FeatureToggleService } from './feature-toggle.service';
import { FeatureFlags } from '../interfaces/feature-flags';
import * as _ from 'lodash';
import { Account} from '@mitel/cloudlink-sdk';
import { GWPIntegrationService } from '../services/gwp-integration.service';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { ActionType, CategoryId } from '../utils/rbac.util';
import { AppStateService } from './app-state.service';
import { AccountsService } from './accounts.service';
import { TenantsService } from './tenants.service';
import { asyncJobStatus } from '../constants/constants';
import { ExternalIntegrationChange, ExternalIntegrationActionType } from '../interfaces/integrations';
@Injectable({
	providedIn: 'root'
})

// this service will hide whatever implementation we eventually have for getting the list of integrations
// types. Right now it is static
export class IntegrationsService {
	private accountIntegrationList: Integration[];
	public integrationsListChanged = new Subject<void>();
	public integrationChanged = new Subject<ExternalIntegrationChange>();

	constructor( private translateService: TranslateService,
				private featureToggleService: FeatureToggleService,
				private gwpService: GWPIntegrationService,
				private rbacAuthGuardService: RBACAuthGuardService,
				private appStateService: AppStateService,
				private tenantsService: TenantsService,
				private accountsService: AccountsService) 
	{
	}

	getMitelIntegrationList( account: Account ): Integration[]
	{
		const supportPortalMode = this.appStateService.getSupportPortalMode();

		// this integration will require knowing what each integration needs
		let completeList: Integration[] = [];
		MitelIntegrations.forEach(element => {
			let newItem = _.cloneDeep( element );
			
			if ( newItem.productCode == 'ContactCenter' )
			{
				// Support person will see it, even if end user is policy denied
				if ( supportPortalMode || this.featureToggleService.showCCNextIntegration() == true ) //policy flag
					completeList.push(newItem);
			}
			if ( (newItem.productCode == 'Workgroups') )
			{
				console.log('Obsolete integration but keeping it in case it`s added back');
			}
			else if ( newItem.productCode == 'MiVoice5000')
			{
				// Support person will see it, even if end user is policy denied
				if ( supportPortalMode || this.featureToggleService.showMitel5000Integration() == true ) //policy flag
					completeList.push(newItem);
			}
			else if ( newItem.productCode == 'Gateway')
			{
				// Support person will see it, even if end user is miaccess denied
				if( supportPortalMode || this.gwpService.hasGWPAccess() == true){
					completeList.push(newItem);
				}
			}
			else if (newItem.productCode == 'MiCC')
			{
				// Support person will see it, even if end user is rbac denied
				if (supportPortalMode || !this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.INTEGRATIONMICC, ActionType.VIEW)) {
					completeList.push(newItem);
				}
			}else if (newItem.productCode == 'MiCollab')
			{
				// Support person will see it, even if end user is rbac denied
				if (supportPortalMode || !this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.INTEGRATIONMICOLLAB, ActionType.VIEW)) {
					completeList.push(newItem);
				}
			}
			else if ( newItem.productCode == 'WorkflowStudio')
			{
				// Support person will see it, even if end user is policy denied
				if (supportPortalMode || (this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_WORKFLOW_STUDIO) == true)) //policy flag
					completeList.push(newItem);
			}
			else if (newItem.productCode == 'UnifyPhone')
			{
				// Support person will see it, even if end user is policy denied
				let isEnabled = this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_UNIFY_PHONE)
				if ( supportPortalMode || isEnabled ) {
					completeList.push(newItem);
				}
			}
			else if (newItem.productCode == 'VoiceAssist') {
					completeList.push(newItem);
			}
			else
			{
				completeList.push(newItem);
			}
		});


		// there can be a hierarchy of integration product
		// here we will COPY the products that are grouped into their parent integration
		for ( var i = 0; i < completeList.length; i++ )
		{
			if ( completeList[i].subIntegrationCodes?.length > 0 )
			{
				let tempIntegration: any = {};
				Object.assign( tempIntegration, completeList[i], {subIntegrationDetails:[]} );

				for ( var k = 0; k < completeList[i].subIntegrationCodes.length; k++ )
				{
					for ( var j = completeList.length-1; j >= 0; j-- )
					{
						if ( completeList[i].subIntegrationCodes[k] == completeList[j].productCode )
						{
							let newItem = _.cloneDeep( completeList[j] );
							tempIntegration.subIntegrationDetails.push(newItem);
						}
					}
				}
				completeList[i] = tempIntegration;
			}
		}
		return completeList;
	}

	getThirdPartyIntegrationList( account: Account ): Integration[]
	{
		const supportPortalMode = this.appStateService.getSupportPortalMode();
		
		// this integration will require knowing what each integration needs
		let completeList: Integration[] = [];

		let syncShow = this.featureToggleService.showADSyncIntegrations();
		ThirdPartyIntegrations.forEach( element => {
			let newItem: Integration  = _.cloneDeep( element );

			if ( newItem.productCode == 'SyncOkta' )
			{
				// Support person will see it, even if end user is policy denied
				if ( supportPortalMode || syncShow == true ) //policy flag
					completeList.push(newItem);
			}
			else if ( newItem.productCode == 'SyncGSuite' )
			{
				// Support person will see it, even if end user is policy denied
				if ( supportPortalMode || this.featureToggleService.showGSuiteDemoIntegration() == true ) //policy flag
					completeList.push(newItem);
			}
			else if (newItem.productCode == 'Twilio')
			{
				if (this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_TWILIO_SMS_NUMBERS_INTEGRATION)) {
					/**
					 * Important. If this feature toggle is removed, the Twilio integration will not be shown to the user.
					 * To always show the Twilio integration to the user, the ThirdPartyIntegrations table entry for 
					 * productCode='Twilio' must be updated to add to the subIntegrationCodes array the value 'Twilio-sms-numbers'.
					 */
					if (newItem.subIntegrationCodes == null) {
						newItem.subIntegrationCodes = [];
					}
					newItem.subIntegrationCodes.push('Twilio-sms-numbers');
				}
				completeList.push(newItem);
			}
			// begin remove when FF removed
			else if (newItem.productCode == 'Zappa')
			{
				let isEnabled = this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_ZAPPA_PHONE)
				if ( supportPortalMode || isEnabled ) {
					completeList.push(newItem);
				}
			}
			// end remove when FF removed
			else
			{
				completeList.push(newItem);
			}
		});

		// there can be a hierarchy of integration product
		// here we will COPY the products that are grouped into their parent integration
		for ( var i = 0; i < completeList.length; i++ )
		{
			if ( completeList[i].subIntegrationCodes?.length > 0 )
			{
				let tempIntegration: any = {};
				Object.assign( tempIntegration, completeList[i], {subIntegrationDetails:[]} );

				for ( var k = 0; k < completeList[i].subIntegrationCodes.length; k++ )
				{
					// Both Mitel and 3rd party intergation can be a sub product
					let newItem: Integration;
					for ( var j = MitelIntegrations.length-1; j >= 0; j-- )
					{
						if ( completeList[i].subIntegrationCodes[k] == MitelIntegrations[j].productCode )
						{
							newItem = _.cloneDeep( MitelIntegrations[j] );
							tempIntegration.subIntegrationDetails.push(newItem);
							break; // exit for loop
						}
					}
					if (!newItem) {
						// check the third party integrations
						for ( var j = ThirdPartyIntegrations.length-1; j >= 0; j-- )
							{
								if ( completeList[i].subIntegrationCodes[k] == ThirdPartyIntegrations[j].productCode )
								{
									newItem = _.cloneDeep( ThirdPartyIntegrations[j] );
									tempIntegration.subIntegrationDetails.push(newItem);
									break;
								}
							}
					}
				}
				completeList[i] = tempIntegration;
			}
		}
		return completeList;
	}

	
	getGeneralPrivilegeList(): Privilege[]
	{
		let completeList: Privilege[] = [];

		GeneralPrivileges.forEach( element => {
			let newItem = Object.assign( {}, element );
			newItem.learnMoreLink = this.translateService.instant(element.learnMoreLink);
			completeList.push(newItem);
		});
		return completeList;
	}

	isAnAccountBasedIntegration( productCode: string ): boolean
	{
		let ndx = AccountBasedIntegrations.findIndex( product => {
			if (product === productCode)
			{
				return true;
			}	
		});		
		return ndx >= 0;
	}

	setAccountIntegrationList(accountIntegrationList) {
		this.accountIntegrationList = accountIntegrationList;
	}

	getAccountIntegrationList(): Integration[] {
		return this.accountIntegrationList;
	}
	
	triggerIntegrationListChange()
	{
		this.integrationsListChanged.next();
	}

	async initializeProductState( product: any, account: Account, isPrimeAccount: boolean ): Promise<any>
	{
		if ( product.service )
		{
			const promises = [];
			if ( product.service.isEligibleForFreeTrial )
			{
				product.freeTrial = {};
				product.freeTrial.granted = false;
				try {
					promises.push(
						product.service.isEligibleForFreeTrial(account).then( result => product.freeTrial.eligible = result )
					);
				} catch {
					// Typically,
				    // 			TypeError: Cannot read properties of undefined (reading 'then')
					// ignore the error.

					// There is a pattern in the integ-bla.service.ts code like this, returning undefined instead of the promised Promise.
					// getConfigButtonLabel(account: Account): Promise<string> {
					// 	return undefined;
					// }
				}
			}

			if ( product.requiresConfiguration )
			{
				if ( isPrimeAccount )
					product.setupComplete = true;
				else
					product.setupComplete = await product.service.isSetupComplete(account);

				if ( !product.usesConfigurationPage )
					product.configurationDialog = product.service.getConfigurationDialog(product.setupComplete);
			}

			if ( product.service.getInfoLink )
			{
				let link = product.service.getInfoLink(account);
				if ( link != undefined )
					product.learnMoreLink = this.translateService.instant(link);
			}

			try {
				promises.push(product.service.getConfigButtonLabel(account).then( result => {

				product.configButtonLabel = result;
				console.log("Integrations service, button label", product, result)}  ))
			} catch { }

			try {
				promises.push(product.service.getStatusString(account).then( result => product.statusString = result ))
			} catch {}

			try {
				promises.push(product.service.getAuxAction(account).then( result => product.auxAction = result ));
			} catch {}
			try {
				promises.push(product.service.needConfigUpdate(account).then(needConfigUpdate => {
					product.needConfigUpdate = needConfigUpdate;
					if (!product.usesConfigurationPage && !product.configurationDialog && needConfigUpdate) {
						product.configurationDialog = product.service.getConfigurationDialog(product.setupComplete);
					}
				}));
			} catch { }
			
			await Promise.all(promises);
		}
		return product;
	}

	async updateIntegrationAsyncStatus(): Promise<void>
	{
		let asyncToProductMap = new Map<string, any>;

		// lets determine if the account can have any async operations
		let account = this.accountsService.getCurrentAccount();
		return this.accountsService.getAccountTagsById(account.accountId).then( async tags => {
			if ( !tags || !tags?.products )
				return; // nothing to do

			let products = tags.products;
			let mitelIntegs = this.getMitelIntegrationList(account);
			let thirdPartyIntegs = await this.getThirdPartyIntegrationList(account);
			for ( var i = 0; i < products.length; i++ ) 
			{
				let product = products[i];
				if ( this.isAnAccountBasedIntegration(product) === true )
				{
					let prodDesc = mitelIntegs.find( elem => {
						return elem.productCode == product;
					});
			
					if ( !prodDesc ) // check for a third party integration
					{
						prodDesc = thirdPartyIntegs.find( elem => {
							return elem.productCode == product;
						});
					}

					if ( prodDesc && prodDesc.service )
					{
						let service: any  = prodDesc.service;
						if ( service && service.canHaveAsyncOperations )
						{
							let canBeAsync = await service.canHaveAsyncOperations(account);
							if ( canBeAsync )
							{
								let jobsInProgress = await service.getAsyncJobIdsInProgress(account);
								if ( jobsInProgress && jobsInProgress.length > 0 )
								{
									jobsInProgress.forEach(jobId => {
										asyncToProductMap.set(jobId, prodDesc);
									});
								}
							}
						}
					}
				}
			}
			if ( asyncToProductMap.size > 0 )
			{
				// get all async jobs - should be able to filter by integration type of async jobs
				// or should I just get each job individually? - It is limited now, might change in the future
				// for now we will get only the jobId in question

				for (const [jobId, product] of asyncToProductMap) 
				{
					try 
					{
						// Here we are only querying the billing async jobs... but it could also query the mivb async jobs if required
						// To think about: In the original PR, YE pointed out that maybe the integration service should be responsible for getting their 
						// own async jobs so this service does not know where to find them. This is a very good point. I would like to see how async
						// integrations operations evolve before I make a change but I do think eventually this should change to a integration operation.
						let job = await this.tenantsService.getAsyncJobById(jobId);

						if ( job && job.status == asyncJobStatus.COMPLETE )
							await product.service.asyncJobIsComplete(account, jobId);
						else if ( job && job.status == asyncJobStatus.FAILED )
						{
							// do not know what to do here yet....How long is the error persistant
							await product.service.asyncJobFailed(account, jobId, job.result);
						}
						else if ( !job ) // is it possible that the job is not there? ... should I assume complete?
						{
							console.log('Checking on async job: ' + jobId + " and it does not exist? - assuming complete")
							await product.service.asyncJobIsComplete(account, jobId);
						}
					}
					catch(err)
					{
						if ( err.statusCode == 404 )
						{
							console.error('Checking on async job: ' + jobId + " and it does not exist? - assuming complete");
							await product.service.asyncJobIsComplete(account, jobId);
						}
						else
						{
							console.error('Checking on async job: ' + jobId + " and it failed with error", err);
							// not sure what to do here - and what type of error it could be
						}
					}
				}
			}
		})
	}
}
